// Models
import { FeedbackAgeGroup, FeedbackAuthenticationType, FeedbackHostPlatformType, FeedbackPolicyValue, FeedbackType, FeedbackUiType, InAppFeedbackScenarioType, launchFeedback, unmountFeedback } from "@ms/inapp-feedback-loader";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { generateLogStringFromError } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { getBrowser } from "@ms/uno-utilities/lib/local/BrowserUtilities";
const FEEDBACK_REF_ID = "inAppFeedbackRef-id";
const HOST_NAME = "Planner";
const PRODUCTION_AUDIENCE = "Production";
const DOGFOOD_AUDIENCE = "Dogfood";
/**
 * Gets or creates a DOM element that will host the feedback dialog
 * @param feedbackRefId - The ID to use for the feedback container element
 * @returns The feedback container element
 */ function getOrCreateFeedbackElement(feedbackRefId) {
    let element = document.getElementById(feedbackRefId ?? FEEDBACK_REF_ID);
    if (!element) {
        element = document.createElement("div");
        element.id = FEEDBACK_REF_ID;
        document.body.appendChild(element);
    }
    return element;
}
/**
 * Maps user age group and consent status to the FeedbackAgeGroup enum
 * @param ageGroup - The user's age group ('Adult', 'NotAdult', 'Minor')
 * @param consentProvidedForMinor - The consent status for minors ('Granted', 'Denied', 'NotRequired')
 * @returns The corresponding FeedbackAgeGroup enum value
 */ export function getUserAgeGroup(ageGroup, consentProvidedForMinor) {
    switch(ageGroup){
        case "Adult":
            return FeedbackAgeGroup.Adult;
        case "NotAdult":
            return FeedbackAgeGroup.NotAdult;
        case "Minor":
            switch(consentProvidedForMinor){
                case "Granted":
                    return FeedbackAgeGroup.MinorWithParentalConsent;
                case "Denied":
                    return FeedbackAgeGroup.MinorWithoutParentalConsent;
                case "NotRequired":
                    return FeedbackAgeGroup.MinorNoParentalConsentRequired;
                default:
                    return FeedbackAgeGroup.MinorWithoutParentalConsent;
            }
        default:
            return FeedbackAgeGroup.Undefined;
    }
}
/**
 * Maps PlannerTheme values to Teams theme values for consistent theming
 * @param theme - The PlannerTheme value to map
 * @returns The corresponding Teams theme string ('TeamsDark', 'TeamsLight', or 'TeamsContrast')
 */ export function getBaseTheme(theme) {
    switch(theme){
        case PlannerTheme.Dark:
            return "TeamsDark";
        case PlannerTheme.Light:
            return "TeamsLight";
        case PlannerTheme.HighContrast:
            return "TeamsHighContrastV2";
        default:
            return "TeamsLight";
    }
}
/** Maps true/false/undefined to Enabled/Disabled/NotConfigured */ export const getFeedbackPolicyValue = (value)=>value == null ? FeedbackPolicyValue.NotConfigured : value ? FeedbackPolicyValue.Enabled : FeedbackPolicyValue.Disabled;
/**
 * Generates feedback configuration options based on the request parameters
 * @param request - The consolidated feedback request parameters
 * @returns Configuration options for the feedback dialog
 */ export function getFeedbackOptions(request) {
    const feedbackOptions = {
        appId: request.appId,
        feedbackConfig: {
            isDisplayed: true,
            feedbackUiType: FeedbackUiType.Modal,
            hostPlatform: FeedbackHostPlatformType.Web,
            initialFeedbackType: undefined,
            isScreenshotEnabled: request.tenantFeedbackSettings?.allowScreenshot,
            complianceChecks: {
                policyAllowFeedback: getFeedbackPolicyValue(request.tenantFeedbackSettings?.allowFeedback),
                policyAllowScreenshot: getFeedbackPolicyValue(request.tenantFeedbackSettings?.allowScreenshot),
                policyAllowCopilotFeedback: getFeedbackPolicyValue(request.tenantFeedbackSettings?.allowCopilotFeedback),
                policyAllowSurvey: getFeedbackPolicyValue(request.tenantFeedbackSettings?.allowSurvey),
                policyEmailCollectionDefault: getFeedbackPolicyValue(request.tenantFeedbackSettings?.allowFollowUp),
                policyAllowContact: getFeedbackPolicyValue(request.tenantFeedbackSettings?.allowFollowUp)
            },
            invokeOnDismissOnEsc: true,
            isEmailCollectionEnabled: request.tenantFeedbackSettings?.allowFollowUp
        },
        themeOptions: {
            isFluentV9: false,
            baseTheme: getBaseTheme(request.theme)
        },
        clientName: request.clientType,
        isProduction: request.isProduction,
        tenantId: request.tenantId ?? undefined,
        telemetry: {
            deviceType: navigator.userAgent,
            platform: navigator.platform,
            audienceGroup: request.audienceGroup ?? undefined,
            sourcePageName: request.currentPage,
            sourcePageURI: window.location.href,
            browser: getBrowser(),
            browserVersion: navigator.appVersion,
            processSessionId: request.processSessionId,
            appVersion: request.appVersion,
            installationType: request.installationType
        },
        authenticationType: FeedbackAuthenticationType.AAD,
        userId: request.userId ?? undefined,
        ageGroup: getUserAgeGroup(request.ageGroup ?? null, request.consentProvidedForMinor ?? null),
        callbackFunctions: {
            getAccessToken: async (scopes, options)=>{
                const claims = options?.claims;
                const token = await request.getAccessToken(Array.isArray(scopes) ? scopes : [
                    scopes
                ], claims);
                return {
                    token: token
                };
            }
        }
    };
    return feedbackOptions;
}
/**
 * Returns the Copilot feedback options for the In App Feedback component.
 * @param request - The input request object.
 */ export function getCopilotFeedbackOptions(request) {
    const { feedbackFeatureArea, feedbackFeatureName, isPositive, theme, tenantFeedbackSettings, appId, isProduction, currentUserId, tenantId, sessionId, locale, getContextData, onDismiss } = request;
    const audienceGroup = isProduction ? PRODUCTION_AUDIENCE : DOGFOOD_AUDIENCE;
    let diagnosticsConfig = {
        isShareContextDataEnabled: false
    };
    if (getContextData != null) {
        diagnosticsConfig = {
            isShareContextDataEnabled: true,
            getContextData: async ()=>{
                const contextData = await getContextData();
                const fileContent = [
                    JSON.stringify(contextData)
                ];
                const file = new File(fileContent, "contextData.json");
                return [
                    file
                ];
            }
        };
    }
    // See https://www.owiki.ms/wiki/OCV/Collecting_Copilot_Feedback for recommendations on how to set these values
    return {
        appId: appId,
        userId: `a:${currentUserId}`,
        tenantId: tenantId,
        authenticationType: FeedbackAuthenticationType.AAD,
        isProduction,
        callbackFunctions: {
            onDismiss
        },
        feedbackConfig: {
            feedbackUiType: FeedbackUiType.Modal,
            isDisplayed: true,
            initialFeedbackType: isPositive ? FeedbackType.Smile : FeedbackType.Frown,
            isScreenshotEnabled: false,
            isScreenRecordingEnabled: false,
            isEmailCollectionEnabled: tenantFeedbackSettings?.allowFollowUp,
            isFeedbackForumEnabled: false,
            isFileUploadEnabled: false,
            diagnosticsConfig,
            scenarioConfig: {
                isScenarioEnabled: true,
                scenarioType: isPositive ? InAppFeedbackScenarioType.AIThumbsUp : InAppFeedbackScenarioType.AIThumbsDown
            },
            complianceChecks: {
                /** See https://www.owiki.ms/wiki/Working_with_feedback_policies for policy setting mappings */ policyAllowFeedback: getFeedbackPolicyValue(tenantFeedbackSettings?.allowFeedback),
                policyAllowScreenshot: getFeedbackPolicyValue(tenantFeedbackSettings?.allowScreenshot),
                policyAllowContact: getFeedbackPolicyValue(tenantFeedbackSettings?.allowFollowUp),
                policyAllowContent: getFeedbackPolicyValue(tenantFeedbackSettings?.allowLogCollection),
                policyAllowSurvey: getFeedbackPolicyValue(tenantFeedbackSettings?.allowSurvey),
                policyEmailCollectionDefault: getFeedbackPolicyValue(request.tenantFeedbackSettings?.allowFollowUp)
            }
        },
        telemetry: {
            audienceGroup,
            featureArea: feedbackFeatureArea,
            featureName: feedbackFeatureName,
            processSessionId: sessionId,
            uILocale: locale
        },
        themeOptions: {
            baseTheme: theme
        }
    };
}
/**
 * Creates initialization data for the feedback dialog
 * @param locale - The user's locale string (e.g., 'en-US')
 * @param inAppFeedbackRef - Optional custom reference ID for the feedback container
 * @returns Initialization data including host name, locale, and DOM reference
 */ export function getFeedbackInitData(locale, inAppFeedbackRef) {
    const inAppFeedbackElement = getOrCreateFeedbackElement(inAppFeedbackRef ?? FEEDBACK_REF_ID);
    return {
        hostName: HOST_NAME,
        locale: locale,
        inAppFeedbackRef: inAppFeedbackElement
    };
}
/**
 * Opens the in-app feedback dialog with the specified configuration
 * @param feedbackInitData - Initialization data for the feedback dialog
 * @param feedbackOptions - Configuration options for the feedback dialog
 * @param loggers - Logger context for telemetry
 * @returns Promise that resolves when the dialog is opened or rejects on error
 */ export async function openInAppFeedback(feedbackInitData, feedbackOptions, loggers) {
    const { traceLogger } = loggers;
    try {
        await launchFeedback(feedbackInitData, feedbackOptions);
    } catch (error) {
        traceLogger.logTrace(0x1e25b417 /* tag_4j1qx */ , TraceLevel.Warning, `Failed to launch in-app feedback [${generateLogStringFromError(error)}]`);
    }
}
/**
 * Unmounts the in-app feedback component
 */ export function unmountInAppFeedback(loggers) {
    const { traceLogger } = loggers;
    try {
        unmountFeedback();
    } catch (error) {
        traceLogger.logTrace(0x1e2597d6 /* tag_4jz5w */ , TraceLevel.Warning, `Failed to unmount in-app feedback [${generateLogStringFromError(error)}]`);
    }
}
